/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Servicio de notificaciones web
 */

const NOTIFICATION_TAG = 'mogotax-conductor';

/**
 * Verificar si las notificaciones están soportadas
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

/**
 * Verificar el estado del permiso de notificaciones
 * @returns {string} - 'granted', 'denied', 'default'
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'denied';
  return Notification.permission;
};

/**
 * Solicitar permiso para notificaciones
 * @returns {Promise<boolean>}
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn('Notificaciones no soportadas en este navegador');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error solicitando permiso de notificaciones:', error);
    return false;
  }
};

/**
 * Mostrar una notificación
 * @param {string} title - Título de la notificación
 * @param {Object} options - Opciones de la notificación
 * @returns {Notification|null}
 */
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported()) return null;

  if (Notification.permission !== 'granted') {
    console.warn('Permiso de notificaciones no concedido');
    return null;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: NOTIFICATION_TAG,
    renotify: true,
    requireInteraction: false,
    vibrate: [200, 100, 200],
  };

  try {
    const notification = new Notification(title, {
      ...defaultOptions,
      ...options,
    });

    // Configurar eventos
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();

      // Navegar a la ruta especificada
      if (options.data?.url) {
        window.location.href = options.data.url;
      }

      notification.close();
    };

    // Auto-cerrar después de 10 segundos
    setTimeout(() => {
      notification.close();
    }, 10000);

    return notification;
  } catch (error) {
    console.error('Error mostrando notificación:', error);
    return null;
  }
};

/**
 * Notificar nuevo traslado asignado
 * @param {Object} traslado - Datos del traslado
 */
export const notifyNewTraslado = (traslado) => {
  const origen = traslado.origen_lugar || traslado.origen_direccion || 'Origen';
  const destino = traslado.destino_lugar || traslado.destino_direccion || 'Destino';
  const hora = traslado.hora_recogida?.slice(0, 5) || '';

  showNotification('Nuevo Traslado Asignado', {
    body: `${origen} → ${destino}${hora ? ` a las ${hora}` : ''}`,
    tag: `traslado-${traslado.id}`,
    data: {
      trasladoId: traslado.id,
      url: `/modules/dashboard/traslado/${traslado.id}`,
    },
  });
};

/**
 * Notificar cambio de estado en traslado
 * @param {Object} traslado - Datos del traslado
 * @param {string} nuevoEstado - Nuevo estado
 */
export const notifyTrasladoStatusChange = (traslado, nuevoEstado) => {
  const estadosNotificables = ['Cancelado', 'Reasignado'];

  if (!estadosNotificables.includes(nuevoEstado)) return;

  const origen = traslado.origen_lugar || 'Traslado';

  showNotification(`Traslado ${nuevoEstado}`, {
    body: `El traslado a ${origen} ha sido ${nuevoEstado.toLowerCase()}`,
    tag: `traslado-status-${traslado.id}`,
    data: {
      trasladoId: traslado.id,
      url: `/modules/Dashboard`,
    },
  });
};

/**
 * Notificar traslado cancelado
 * @param {Object} traslado - Datos del traslado
 */
export const notifyTrasladoCancelado = (traslado) => {
  const origen = traslado.origen_lugar || traslado.origen_direccion || 'Origen';

  showNotification('Traslado Cancelado', {
    body: `El traslado desde ${origen} ha sido cancelado`,
    tag: `traslado-cancelado-${traslado.id}`,
    data: {
      trasladoId: traslado.id,
      url: `/modules/Dashboard`,
    },
  });
};

/**
 * Notificar recordatorio de traslado
 * @param {Object} traslado - Datos del traslado
 * @param {number} minutosAntes - Minutos antes del traslado
 */
export const notifyTrasladoReminder = (traslado, minutosAntes = 30) => {
  const origen = traslado.origen_lugar || traslado.origen_direccion || 'Origen';
  const hora = traslado.hora_recogida?.slice(0, 5) || '';

  showNotification('Recordatorio de Traslado', {
    body: `Traslado en ${minutosAntes} minutos: ${origen}${hora ? ` a las ${hora}` : ''}`,
    tag: `traslado-reminder-${traslado.id}`,
    requireInteraction: true,
    data: {
      trasladoId: traslado.id,
      url: `/modules/dashboard/traslado/${traslado.id}`,
    },
  });
};

/**
 * Configurar integración con WebSocket
 * @param {Object} socketContext - Contexto de Socket
 */
export const setupNotificationListeners = (socketContext) => {
  if (!socketContext) return;

  const {
    onTrasladoAsignado,
    onTrasladoCancelado,
    onTrasladoStatusChange,
  } = socketContext;

  // Escuchar nuevos traslados asignados
  if (onTrasladoAsignado) {
    onTrasladoAsignado(({ traslado }) => {
      if (traslado) {
        notifyNewTraslado(traslado);
      }
    });
  }

  // Escuchar traslados cancelados
  if (onTrasladoCancelado) {
    onTrasladoCancelado(({ traslado }) => {
      if (traslado) {
        notifyTrasladoCancelado(traslado);
      }
    });
  }

  // Escuchar cambios de estado
  if (onTrasladoStatusChange) {
    onTrasladoStatusChange(({ traslado }) => {
      if (traslado) {
        notifyTrasladoStatusChange(traslado, traslado.status);
      }
    });
  }
};

const notificationService = {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  notifyNewTraslado,
  notifyTrasladoStatusChange,
  notifyTrasladoCancelado,
  notifyTrasladoReminder,
  setupNotificationListeners,
};

export default notificationService;
